import React, { useState } from "react";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Button, Col, Container, FloatingLabel, Form, Row } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from "ag-grid-community";
import * as XLSX from "xlsx";
import SalesStatisticsStore from "../stores/SalesStatisticsStore";
import StudioUserStore from "../stores/StudioUserStore";
import BlockKitStore from "../stores/BlockKitStore";
import { SalesThemeDetailModel } from "../controllers/SalesStatisticsController";
import { getLocaleString } from "../utils/DateHelper";
import { ModeJsonModel, LicenseJsonModel } from "../models/BlockTransportSystemJsonModel";
import { LanguageCode } from "../constants/LanguageCode";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface Props {
  salesStatisticsStore?: SalesStatisticsStore;
  studioUserStore?: StudioUserStore;
  blockKitStore?: BlockKitStore;
}

const SalesDetailPage: React.FC<Props> = inject("salesStatisticsStore", "studioUserStore", "blockKitStore")(observer(({ salesStatisticsStore, studioUserStore, blockKitStore }) => {
  const { t } = useTranslation();
  const me = studioUserStore?.me;
  const [modes, setModes] = useState<ModeJsonModel[]>([]);
  const [licenses, setLicenses] = useState<LicenseJsonModel[]>([]);

  React.useEffect(() => {
    blockKitStore?.studioController.getUserModesAsync().then(setModes);
    blockKitStore?.studioController.getUserLicensesAsync().then(setLicenses);
  }, []);

  const today = new Date().toISOString().split("T")[0];
  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today);
  const [searchedStartDate, setSearchedStartDate] = useState("");
  const [searchedEndDate, setSearchedEndDate] = useState("");
  const [selectedLocale, setSelectedLocale] = useState<LanguageCode | "">("");
  const [selectedModeId, setSelectedModeId] = useState<string>("");
  const [selectedLicenseId, setSelectedLicenseId] = useState<string>("all");
  const [selectedRows, setSelectedRows] = useState<SalesThemeDetailModel[]>([]);
  const gridRef = React.useRef<AgGridReact>(null);

  React.useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [blockKitStore?.timeZone]);

  const isDateRangeValid = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    return diffMonths < 3 || (diffMonths === 3 && end.getDate() <= start.getDate());
  };

  const handleSearch = async () => {
    if (!isDateRangeValid()) {
      alert(t("date-range-limit"));
      return;
    }
    setSelectedRows([]);
    await salesStatisticsStore?.fetchDetailAsync({
      startDate,
      endDate,
      locale: selectedLocale,
      modeId: selectedModeId,
      licenseId: selectedLicenseId,
    });
    setSearchedStartDate(startDate);
    setSearchedEndDate(endDate);
  };

  const handleExcelDownload = () => {
    if (selectedRows.length === 0) {
      alert(t("no-selected"));
      return;
    }
    const prefix = t("nav-sales-detail");
    const fileName = `${prefix}_${searchedStartDate}_${searchedEndDate}.xlsx`;

    const rows = selectedRows.map((row: SalesThemeDetailModel) => ({
      [t("col-preview")]: row.previewUrl,
      [t("col-sales-time")]: getLocaleString(new Date(row.createTime), blockKitStore?.timeZone),
      [t("col-sales-id")]: row.salesId,
      [t("col-vending-machine-id")]: row.vendingMachineId,
      [t("col-vending-machine")]: row.vendingMachineName,
      [t("col-group")]: row.groupName,
      [t("col-block-id")]: row.blockId,
      [t("col-name")]: row.blockName,
      [t("col-subject-name")]: row.subjectName,
      [t("col-block-type")]: row.blockType,
      [t("col-category-id")]: row.categoryId,
      [t("col-category")]: row.categoryName,
      [t("col-license-id")]: row.licenseId,
      [t("col-license")]: row.licenseName,
      [t("col-set-product-type")]: row.setProductType,
      [t("col-set-index")]: row.setIndex,
      [t("col-set-count")]: row.setCount,
      [t("col-set-unit-price")]: row.setCount ? row.setPrice / row.setCount : 0,
      [t("col-set-price")]: row.setPrice,
      [t("col-order-sales-price")]: row.salesPrice,
      [t("col-order-sales-origin-price")]: row.salesOriginPrice,
      [t("col-order-sales-discountable-price")]: row.salesDiscountablePrice,
      [t("col-order-sales-count")]: row.salesCount,
      [t("col-sales-coupon-key")]: row.salesCouponKey,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("sheet-summary").slice(0, 31));
    XLSX.writeFile(wb, fileName);
  };

  const PreviewCell = (props: ICellRendererParams) => (
    props.value ? (
      <a href={props.value} target="_blank" rel="noopener noreferrer">
        <img src={props.value} alt="배너" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
      </a>
    ) : null
  );

  const centerCellStyle = { textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center" };
  const setGroupCellStyle = { ...centerCellStyle, backgroundColor: "#FFE8C2" };
  const orderGroupCellStyle = { ...centerCellStyle, backgroundColor: "#D6E6FF" };

  return (
    <Container fluid style={{ backgroundColor: "#F6F7F8", minHeight: "100vh" }}>
      <style>{`
        .ag-header-cell-label { justify-content: center; }
        .set-group-header { background-color: #FFE8C2; }
        .order-group-header { background-color: #D6E6FF; }
      `}</style>

      {/* 필터 */}
      <Row className="g-2 mb-2 pt-3 fs-6">
        <Col md>
        {/* 조회 시작일 */}
          <FloatingLabel label={t("start-date")}>
            <Form.Control
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 조회 종료일 */}
          <FloatingLabel label={t("end-date")}>
            <Form.Control
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
            />
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 국가 */}
          <FloatingLabel label={t("locale")}>
            <Form.Select onChange={e => setSelectedLocale(e.target.value as LanguageCode | "")}>
              <option value="">ALL</option>
              {me?.allowedLocales.map(locale => (
                <option key={locale} value={locale}>{locale}</option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 고객사(스크린) */}
          <FloatingLabel label={t("client")}>
            <Form.Select onChange={e => setSelectedModeId(e.target.value)}>
              <option value="">ALL</option>
              {modes.map(mode => {
                return (
                  <option key={mode.id} value={mode.id}>
                    {mode?.name ?? mode.id}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md>
        {/* 라이선스 */}
          <FloatingLabel label={t("license")}>
            <Form.Select value={selectedLicenseId} onChange={e => setSelectedLicenseId(e.target.value)}>
              <option value="all">ALL</option>
              <option value="">{t("license-none")}</option>
              {me?.allowedLicenseIds.filter(Boolean).map(licenseId => {
                const license = licenses.find(l => l.id === licenseId);
                return (
                  <option key={licenseId} value={licenseId}>
                    {license?.name ?? licenseId}
                  </option>
                );
              })}
            </Form.Select>
          </FloatingLabel>
        </Col>
        <Col md="auto" className="d-flex justify-content-center align-items-center">
        {/* "조회중..." : "검색" */}
          <Button size="sm" onClick={handleSearch} disabled={salesStatisticsStore?.isDetailLoading}>
            {salesStatisticsStore?.isDetailLoading ? t("searching") : t("search")}
          </Button>
        </Col>
      </Row>

    <div style={{ backgroundColor: "#fff", borderRadius: "8px", padding: "12px 16px" }}>
      {/* 조회 기간 + 엑셀 버튼 */}
      {/* 상품/주문 안내(좌, 표와 좌측 정렬) + 조회기간 + 엑셀 버튼 */}
        <div className="d-flex justify-content-between align-items-end mb-2">
          <div className="text-muted d-flex flex-column gap-1" style={{ fontSize: "0.78rem", border: "1px solid #e0e0e0", borderRadius: 6, padding: "6px 10px" }}>
            <span className="d-flex align-items-start">
              <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 2, marginRight: 6, marginTop: 2, flexShrink: 0, border: "1px solid #E0B877", backgroundColor: setGroupCellStyle.backgroundColor }} />
              {t("sales-detail-note-set")}
            </span>
            <span className="d-flex align-items-start">
              <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 2, marginRight: 6, marginTop: 2, flexShrink: 0, border: "1px solid #A6C5F2", backgroundColor: orderGroupCellStyle.backgroundColor }} />
              {t("sales-detail-note-order")}
            </span>
          </div>
          <div className="d-flex align-items-center gap-3">
            {searchedStartDate && searchedEndDate && (
              <span className="fw-bold" translate="no">
                {searchedStartDate} ~ {searchedEndDate}
              </span>
            )}
            <Button
              size="sm"
              variant="success"
              onClick={handleExcelDownload}
            >
              Excel
            </Button>
          </div>
        </div>

      {/* 테이블 */}
      <div style={{ height: "70vh" }}>
        <AgGridReact<SalesThemeDetailModel>
          ref={gridRef}
          getRowId={params => params.data.salesId}
          rowData={salesStatisticsStore?.detailData}
          defaultColDef={{
            width: 200,
            cellStyle: centerCellStyle
          }}
          rowSelection={{ mode: "multiRow" }}
          onSelectionChanged={e => setSelectedRows(e.api.getSelectedRows())}
          columnDefs={[
            {
              headerName: t("col-preview"), // 배너
              field: "previewUrl",
              width: 90,
              cellRenderer: PreviewCell,
            },
            {
              headerName: t("col-sales-time"), // 판매일시
              field: "createTime",
              filter: "agDateColumnFilter",
              valueFormatter: (params: any) => getLocaleString(new Date(params.data.createTime), blockKitStore?.timeZone),
            },
            {
              headerName: t("col-sales-id"), // 판매ID
              field: "salesId",
              filter: true,
            },
            {
              headerName: t("col-vending-machine-id"), // 매장ID
              field: "vendingMachineId",
              filter: true,
            },
            {
              headerName: t("col-vending-machine"), // 매장
              field: "vendingMachineName",
              filter: true,
            },
            {
              headerName: t("col-group"), // 그룹
              field: "groupName",
              filter: true,
            },
            {
              headerName: t("col-block-id"), // 디자인ID
              field: "blockId",
              filter: true,
            },
            {
              headerName: t("col-name"), // 이름
              field: "blockName",
              filter: true,
            },
            {
              headerName: t("col-subject-name"), // 주제명
              field: "subjectName",
              filter: true,
            },
            {
              headerName: t("col-block-type"), // 디자인종류(타입)
              field: "blockType",
              filter: true,
            },
            {
              headerName: t("col-category-id"), // 카테고리ID
              field: "categoryId",
              filter: true,
            },
            {
              headerName: t("col-category"), // 카테고리
              field: "categoryName",
              filter: true,
            },
            {
              headerName: t("col-license-id"), // 라이선스ID
              field: "licenseId",
              filter: true,
            },
            {
              headerName: t("col-license"), // 라이선스
              field: "licenseName",
              filter: true,
            },
            {
              headerName: t("col-product-type"), // 상품타입
              field: "setProductType",
              filter: true,
              cellStyle: setGroupCellStyle,
              headerClass: "set-group-header",
            },
            {
              headerName: t("col-set-index"), // 상품순번
              field: "setIndex",
              width: 110,
              filter: true,
              cellStyle: setGroupCellStyle,
              headerClass: "set-group-header",
            },
            {
              headerName: t("col-set-count"), // 상품수량
              field: "setCount",
              width: 110,
              filter: true,
              cellStyle: setGroupCellStyle,
              headerClass: "set-group-header",
            },
            {
              headerName: t("col-set-unit-price"), // 상품단가 (상품가격 / 상품수량)
              colId: "setUnitPrice",
              width: 110,
              filter: true,
              valueGetter: (params: any) => params.data?.setCount ? params.data.setPrice / params.data.setCount : 0,
              cellStyle: setGroupCellStyle,
              headerClass: "set-group-header",
            },
            {
              headerName: t("col-set-price"), // 상품가격
              field: "setPrice",
              width: 110,
              filter: true,
              cellStyle: setGroupCellStyle,
              headerClass: "set-group-header",
            },
            {
              headerName: t("col-order-sales-price"), // 주문판매가
              field: "salesPrice",
              width: 120,
              filter: true,
              cellStyle: orderGroupCellStyle,
              headerClass: "order-group-header",
            },
            {
              headerName: t("col-order-sales-origin-price"), // 주문정가
              field: "salesOriginPrice",
              width: 120,
              filter: true,
              cellStyle: orderGroupCellStyle,
              headerClass: "order-group-header",
            },
            {
              headerName: t("col-order-sales-discountable-price"), // 주문할인액
              field: "salesDiscountablePrice",
              width: 120,
              filter: true,
              cellStyle: orderGroupCellStyle,
              headerClass: "order-group-header",
            },
            {
              headerName: t("col-order-sales-count"), // 주문판매수량
              field: "salesCount",
              width: 120,
              filter: true,
              cellStyle: orderGroupCellStyle,
              headerClass: "order-group-header",
            },
            {
              headerName: t("col-sales-coupon-key"), // 쿠폰
              field: "salesCouponKey",
              filter: true,
              cellStyle: orderGroupCellStyle,
              headerClass: "order-group-header",
            },
          ]}
        />
      </div>
    </div>
    </Container>
  );
}));

export default SalesDetailPage;
